import { LocalDate, LocalTime, OffsetDateTime, ZoneId } from "@js-joda/core";

const FALLBACK_TIME_ZONE = "Europe/Stockholm";

/** Converts a time value to local time in the given zone. */
export const toLocalTime = (value: string, zone: ZoneId): LocalTime => {
  if (!value) throw new Error("Invalid time");
  if (/^\d{2}:\d{2}$/.test(value)) return LocalTime.parse(value);
  const offsetDateTime = OffsetDateTime.parse(value);
  return offsetDateTime.atZoneSameInstant(zone).toLocalTime();
};

/** Safe variant of toLocalTime that returns null on parse failure. */
export const toLocalTimeSafe = (
  value: string,
  zone: ZoneId,
): LocalTime | null => {
  try {
    return toLocalTime(value, zone);
  } catch {
    return null;
  }
};

/** Parses an ISO offset datetime string and returns null on failure. */
export const toOffsetDateTimeSafe = (value: string): OffsetDateTime | null => {
  try {
    return OffsetDateTime.parse(value);
  } catch {
    return null;
  }
};

/** Capitalizes first letter and lowercases the rest. */
export const toSentenceCase = (value: string): string => {
  if (!value) return value;
  return `${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`;
};

/** Returns true when end time is the same day as start time. */
export const endsSameDay = (start: string, end: string): boolean => {
  const startTime = LocalTime.parse(start);
  const endTime = LocalTime.parse(end);
  return !endTime.isBefore(startTime);
};

/** Builds an ISO instant string from local date/time in a given zone. */
export const toIsoFromLocal = (
  date: LocalDate,
  localTime: string,
  zoneId: ZoneId,
): string => {
  return date
    .atTime(LocalTime.parse(localTime))
    .atZone(zoneId)
    .toInstant()
    .toString();
};

/** Normalizes loose user time input to HH:mm or returns null if invalid. */
export const normalizeTimeValue = (value: string): string | null => {
  const trimmed = value.trim();
  if (/^\d{1,2}$/.test(trimmed)) {
    const hours = Number.parseInt(trimmed, 10);
    if (hours < 0 || hours > 23) return null;
    return `${String(hours).padStart(2, "0")}:00`;
  }

  if (/^\d{1,2}:\d{2}$/.test(trimmed)) {
    const [hoursText, minutesText] = trimmed.split(":");
    const hours = Number.parseInt(hoursText, 10);
    const minutes = Number.parseInt(minutesText, 10);
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0",
    )}`;
  }

  return null;
};

/** Safely parses a ZoneId and falls back to the default company zone. */
export const parseZoneId = (zoneIdValue: string): ZoneId => {
  try {
    return ZoneId.of(zoneIdValue);
  } catch {
    return ZoneId.of(FALLBACK_TIME_ZONE);
  }
};
